<template>
  <div>
    <div v-if="loading" class="dot-opacity-loader">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <div v-if="statesStat && statesStat.length">
      <template v-for="(c, i) in charts">
        <div :key="i">
          <b-row class="align-item-center justify-content-center p-3">
            <h3 v-if="c.name && byShift" class="title text-center">
              {{ c.name }}
            </h3>
          </b-row>
          <b-row>
            <b-col class="p-0" cols="12" md="7">
              <h4 class="mt-2 mb-3 text-center">
                {{ stateChartText }}
              </h4>
              <apexchart
                v-if="!loading"
                :height="windowWidth > 1400 ? '265' : '350'"
                :options="c.chart.options"
                :series="c.chart.series"
              ></apexchart>
            </b-col>
            <b-col class="p-0" cols="12" md="5">
              <box-efficiency-chart
                :height="windowWidth > 1400 ? '305' : '260'"
                :period="period"
                :target-efficiency="targetEfficiency"
                :actual-efficiency-value="
                  byShift ? actualEfficiencyByShift(c.shift) : actualEfficiency
                "
                :label="efficiencyChartText"
              />
            </b-col>
          </b-row>
        </div>
      </template>
    </div>
    <div v-else class="row justify-content-center">
      <h4 class="p-3">{{ $t('dashboards.noData') }}</h4>
    </div>
  </div>
</template>

<script>
import { vueWindowSizeMixin } from 'vue-window-size';
import workCenterStatesStat from '@/api/boxinfo/workCenterStatesStat.gql';
import workCenterStatesStatByShift from '@/api/boxinfo/workCenterStatesStatByShift.gql';
import moment from 'moment';

const DEFAULT_EFFICIENCY = 85;

export default {
  name: 'StatesChart',
  components: {
    apexchart: () => import('vue-apexcharts'),
  },
  mixins: [vueWindowSizeMixin],
  props: {
    period: Object,
    item: Object,
    byShift: Boolean,
    tabPeriodText: String,
    minPeriod: Number,
    maxPeriod: Number,
  },
  data: function () {
    return {
      statesStat: [],
      stateIdOff: null,
    };
  },
  apollo: {
    statesStat: {
      query() {
        return this.byShift ? workCenterStatesStatByShift : workCenterStatesStat;
      },
      variables() {
        return {
          workCenterId: this.workCenterId,
          period: this.periodData,
        };
      },
      pollInterval: 20000,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        if (queryData) {
          return queryData[Object.keys(queryData)[0]];
        } else {
          return [];
        }
      },
      skip() {
        return !this.workCenterId || !this.period;
      },

      fetchPolicy: 'cache-and-network',
    },
  },
  computed: {
    periodData() {
      return this.minPeriod && this.maxPeriod
        ? {
            dateStart: moment(this.minPeriod).format(),
            dateEnd: moment(this.maxPeriod).format(),
          }
        : this.period;
    },
    workCenterId() {
      return (((this.item || {}).digitalThing || {}).workCenter || {}).id;
    },
    targetEfficiency() {
      return (
        ((((this.item || {}).digitalThing || {}).workCenter || {}).efficiencyParam || {})
          .targetEfficiency || DEFAULT_EFFICIENCY
      );
    },
    actualEfficiency() {
      return this.computeEfficiency(this.statesStat);
    },
    stateChartText() {
      let label = this.$t('boxInfo.workCenterState.title');

      if (this.byShift) {
        return label;
      }

      return label + this.$t('dashboards.for') + this.tabPeriodText;
    },
    efficiencyChartText() {
      if (this.byShift) {
        return this.$t('box.efficiency');
      }
      return this.$t('box.efficiency') + this.$t('dashboards.for') + this.tabPeriodText;
    },
    hoursText() {
      return this.$t('dashboards.hours');
    },
    minutesText() {
      return this.$t('dashboards.minutes');
    },
    statesStatByshifts() {
      return this.groupBy(this.statesStat, (i) => i.shiftId);
    },
    charts() {
      const res = [];
      this.statesStatByshifts.forEach((v, k) => {
        res.push({
          name: k ? `${this.$t('box.shift')} ${k}` : '',
          shift: k,
          chart: this.getChart(v),
        });
      });
      return res;
    },
    loading() {
      return this.$apollo.queries.statesStat.loading;
    },
  },
  methods: {
    actualEfficiencyByShift(shift) {
      return this.computeEfficiency(this.statesStatByshifts.get(shift));
    },
    setStateId(data) {
      this.stateIdOff = this.stateIdOff === data ? null : data;
      this.$emit('stateId', this.stateIdOff);
    },
    getChart(items) {
      const self = this;
      const sortedItems = items.map((i) => {
        const sortNum =
          i.type == 'WORK_WITH_LOAD'
            ? i.stateId == 'S16'
              ? 1
              : 2
            : i.type == 'WORK_WITHOUT_LOAD'
            ? 3
            : i.stateId != 'S6'
            ? 4
            : 5;
        return {
          ...i,
          sortNum,
        };
      });
      sortedItems.sort((a, b) => (a.sortNum > b.sortNum ? 1 : b.sortNum > a.sortNum ? -1 : 0));
      function formatterTime(val) {
        const hours = Math.floor(val).toLocaleString();
        const minutes = Math.floor((val % 1) * 60).toLocaleString();
        return `${hours} ${hoursText} ${minutes} ${minutesText}`;
      }
      const hoursText = this.hoursText;
      const minutesText = this.minutesText;
      const colors = sortedItems.map((i) => (i.stateColor ? i.stateColor.trim() : '#6D6E71'));
      const labels = sortedItems.map((i) => i.stateName);
      const overall = this.$t('dashboards.overall');
      return {
        series: sortedItems.map((i) => i.duration / 60),
        options: {
          chart: {
            events: {
              dataPointSelection: function (event, chartContext, config) {
                self.setStateId(sortedItems[config.dataPointIndex].stateId);
              },
            },
            animations: {
              enabled: false,
            },
            type: 'donut',
          },
          plotOptions: {
            pie: {
              donut: {
                background: '#293450',
                labels: {
                  show: true,
                  value: {
                    show: true,
                    fontSize: '30px',
                    color: '#fff',
                    formatter: function (val) {
                      return formatterTime(val);
                    },
                  },
                  total: {
                    show: true,
                    showAlways: true,
                    color: '#fff',
                    fontSize: '21px',
                    label: overall,
                    formatter: function (w) {
                      const val = w.globals.seriesTotals.reduce((a, b) => {
                        return a + b;
                      }, 0);
                      return formatterTime(val);
                    },
                  },
                },
              },
            },
          },
          responsive: [
            {
              breakpoint: 900,
              options: {
                legend: {
                  width: 0,
                  position: 'bottom',
                },
              },
            },
            {
              breakpoint: 1400,
              options: {
                plotOptions: {
                  pie: {
                    donut: {
                      labels: {
                        value: {
                          fontSize: '18px',
                        },
                      },
                    },
                  },
                },
                legend: {
                  width: 0,
                  position: 'bottom',
                },
                dataLabels: {
                  style: {
                    fontSize: '13px',
                    colors: ['#fff'],
                  },
                },
              },
            },
          ],
          colors,
          labels,
          legend: {
            width: 350,
            position: 'right',
            showForSingleSeries: true,
            fontSize: '15px',
            formatter: function (seriesName, opts) {
              return seriesName + ' ' + formatterTime(opts.w.globals.series[opts.seriesIndex]);
            },
            labels: {
              colors: 'white',
              useSeriesColors: false,
            },
            itemMargin: {
              vertical: 3,
            },
          },
          tooltip: {
            theme: 'dark',
            fillSeriesColor: false,
            x: {
              show: false,
            },
            y: {
              formatter: function (value) {
                return formatterTime(value);
              },
            },
          },
          dataLabels: {
            style: {
              fontSize: '19px',
              colors: ['#fff'],
            },
            background: {
              enabled: false,
            },
            dropShadow: {
              enabled: true,
            },
          },
          stroke: {
            show: false,
          },
        },
      };
    },
    groupBy(list, keyGetter) {
      const map = new Map();
      list.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
          map.set(key, [item]);
        } else {
          collection.push(item);
        }
      });
      return map;
    },
    computeEfficiency(stateStats) {
      if (!stateStats || !stateStats.length) {
        return 0;
      }
      let loadTime = 0;
      let overallTime = 0;
      stateStats.forEach((i) => {
        if (i.type == 'WORK_WITH_LOAD') {
          loadTime += i.duration;
        }
        overallTime += i.duration;
      });

      return overallTime != 0 ? (100 * loadTime) / overallTime : 0;
    },
  },
};
</script>
